import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
// Remove EntityTag and TagValue imports - legacy system no longer used
import { extendSchemaWithJsonAttributes } from "src/utils/zodDynamicSchema.ts";
import { useFoodpilotContext, INGREDIENT } from "src/context/FoodpilotContext.tsx";
import { isQueryReady } from "src/api/axiosClient/utils";
import { Campaign, CampaignSchema } from "src/api/client-api/useCampaign";
import { getRemoteEntityState } from "src/api/remoteEntityState";
import type { RemoteErrorStatus } from "src/api/remoteCollectionState";

type Options = {
    organization_id?: number;
};

export const getOneIngredientKey = "getOneIngredient" as const;

export type IngredientLoadState =
    | { status: "loading" }
    | { status: "success-value"; ingredient: Ingredient; refreshError?: RemoteErrorStatus }
    | { status: "not-found" }
    | { status: "forbidden" }
    | { status: "contract-error" }
    | { status: "server-error" };

type IngredientLoadStateInput = {
    ingredientId: string | undefined;
    ingredient: Ingredient | undefined;
    isLoading: boolean;
    error: unknown;
};

export const getIngredientLoadState = (input: IngredientLoadStateInput): IngredientLoadState => {
    const state = getRemoteEntityState({
        entity: input.ingredient,
        entityId: input.ingredientId,
        error: input.error,
        isLoading: input.isLoading,
    });

    return state.status === "success-value" ?
            { status: "success-value", ingredient: state.entity, refreshError: state.refreshError }
        :   state;
};

export const useGetOneIngredient = (ingredientId: string | undefined, options?: Options) => {
    const { entitiesJsonAttributes, currentCampaign } = useFoodpilotContext();
    const { organization_id } = options || {};
    const campaignId = currentCampaign?.id;

    const isEnabled = ingredientId !== undefined;
    const query = useQuery({
        queryKey: [getOneIngredientKey, ingredientId, organization_id, campaignId],
        cacheTime: 0,
        enabled: isEnabled,
        queryFn: async () => {
            const url = `/api/ingredients/${ingredientId}`;

            return await loggedInApiCall.get<Ingredient>(url, {
                params: {
                    organization_id,
                    campaign: campaignId,
                },
            });
        },
        select: (request) => {
            const finalSchema = extendSchemaWithJsonAttributes(IngredientZodSchema, entitiesJsonAttributes, INGREDIENT);

            return zodParseWithDetailedLog(request.data, finalSchema, {
                operation: "GET",
                endpoint: "/ingredients/:id",
            }) as IngredientWithAttributes;
        },
    });

    const isReady = isQueryReady(query, isEnabled);
    const ingredientLoadState = getIngredientLoadState({
        ingredientId,
        ingredient: query.data,
        isLoading: query.isLoading,
        error: query.error,
    });

    return {
        ingredient: query.data,
        ingredientLoadState,
        isReady,
        ...query,
    };
};

type Domain = {
    "@id": IRI;
    name: string;
};
const DomainSchema: z.ZodSchema<Domain> = z.object({
    "@id": z.string(),
    name: z.string(),
});

export type IngredientLine = {
    "@id": IRI;
    id: number;
    name: string;
};
const IngredientLineZodSchema: z.ZodSchema<IngredientLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

type OwnedBy = {
    "@id": string;
    id: number;
    name: string;
};

const OwnedBySchema: z.ZodSchema<OwnedBy> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

type IngredientCompany = {
    "@id": IRI;
    id: number;
    organization: OwnedBy;
};

const IngredientCompanySchema: z.ZodSchema<IngredientCompany> = z.object({
    "@id": z.string(),
    id: z.number(),
    organization: OwnedBySchema,
});

export type CompanyFromIngSupplier = {
    "@id": IRI;
    id: number;
    name: string;
    clientId?: string;
};
const CompanyFromIngSupplierSchema: z.ZodSchema<CompanyFromIngSupplier> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    clientId: z.string().optional(),
});

type IngredientFromIngSupplier =
    | IRI
    | {
          id: number;
          ingredientLine?: IngredientLine;
      };

const IngredientFromIngSupplierSchema: z.ZodSchema<IngredientFromIngSupplier> = z.union([
    z.string(),
    z.object({
        id: z.number(),
        ingredientLine: IngredientLineZodSchema.optional(),
    }),
]);

export type IngredientSupplierActivityData = {
    id: number;
    "@id": IRI;
    campaign: Campaign;
    organization: CompanyFromIngSupplier;
    grossSales?: number;
    quantity?: number;
};
const IngredientSupplierActivityDataSchema: z.ZodSchema<IngredientSupplierActivityData> = z.object({
    id: z.number(),
    "@id": z.string(),
    campaign: CampaignSchema,
    organization: CompanyFromIngSupplierSchema,
    grossSales: z.number().optional(),
    quantity: z.number().optional(),
});

/**
 * Lightweight projection of an IngredientReference returned in the `ingredient_supplier:read`
 * group. Carries only what the form needs to display / preselect the per-supplier
 * reference (`@id` for resubmit, `name` for the autocomplete label).
 */
export type IngredientReferenceLine = {
    "@id": IRI;
    id: number;
    name: string;
};

const IngredientReferenceLineSchema: z.ZodSchema<IngredientReferenceLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type IngredientSupplier = {
    "@id": IRI;
    id: number;
    ingredient: IngredientFromIngSupplier;
    supplier: CompanyFromIngSupplier;
    managedBy: CompanyFromIngSupplier;
    ingredientSupplierActivityDatas?: IngredientSupplierActivityData[];
    unit?: string;
    ingredientReference?: IngredientReferenceLine | null;
};
const IngredientSupplierSchema: z.ZodSchema<IngredientSupplier> = z.object({
    "@id": z.string(),
    id: z.number(),
    ingredient: IngredientFromIngSupplierSchema,
    supplier: CompanyFromIngSupplierSchema,
    managedBy: CompanyFromIngSupplierSchema,
    ingredientSupplierActivityDatas: IngredientSupplierActivityDataSchema.array().optional(),
    unit: z.string().optional(),
    ingredientReference: IngredientReferenceLineSchema.nullable().optional(),
});

export type Ingredient = {
    "@id": IRI;
    id: number;
    name: string;
    clientId?: string;
    domains?: Domain[];
    externalId?: number;
    suppliers?: IngredientSupplier[];
    shares?: IngredientCompany[];
    ingredientLine?: IngredientLine;
    ingredientReferenceId?: number;
    referenceSource?: "sure" | "foodpilot";
    organic?: boolean;
    canModify: boolean;
    canDelete: boolean;
    prevCampaignName?: string;
    ownedBy?: OwnedBy;
    tags?: Record<string, string[]>; // Format objet groupé par type de tag
    jsonAttributes?: Record<string, any>;
};

type IngredientWithAttributes = Ingredient & { [key: string]: Record<string, string | number | boolean> };

// Schéma pour accepter soit un objet soit un tableau vide (API Platform)
const IngredientTagsSchema = z
    .union([
        z.record(z.string(), z.array(z.string())), // Format objet standard
        z.array(z.never()).length(0), // Tableau vide uniquement
    ])
    .transform((value): Record<string, string[]> => {
        // Convertir tableau vide en objet vide pour cohérence
        return Array.isArray(value) ? {} : value;
    }) as z.ZodType<Record<string, string[]>>;

export const IngredientZodSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    clientId: z.string().optional(),
    domains: DomainSchema.array().optional(),
    externalId: z.number().optional(),
    suppliers: IngredientSupplierSchema.array().optional(),
    shares: IngredientCompanySchema.array().optional(),
    ingredientLine: IngredientLineZodSchema.optional(),
    ingredientReferenceId: z.number().optional(),
    referenceSource: z.union([z.literal("sure"), z.literal("foodpilot")]).optional(),
    organic: z.boolean().optional(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    prevCampaignName: z.string().optional(),
    ownedBy: OwnedBySchema.optional(),
    tags: IngredientTagsSchema.optional(), // Accepte objet ou tableau vide
    jsonAttributes: z
        .union([z.record(z.string(), z.any()), z.array(z.any())])
        .optional()
        .transform((val) => {
            // Si c'est un tableau, le convertir en objet vide pour compatibilité
            if (Array.isArray(val)) {
                return {};
            }
            return val;
        }),
});
